// Tag/chip input for enum property values.
// Renders a list of removable chips plus an inline text field.
// Press Enter or click "Add" to add a new value; click × to remove.

import { useRef, useState } from "react";
import { X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

interface EnumValuesInputProps {
  values: string[];
  onChange: (values: string[]) => void;
  disabled?: boolean;
}

export function EnumValuesInput({ values, onChange, disabled }: EnumValuesInputProps) {
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  function addValue() {
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    if (values.includes(trimmed)) {
      // Duplicate — clear input but don't add
      setInputValue("");
      return;
    }
    onChange([...values, trimmed]);
    setInputValue("");
    inputRef.current?.focus();
  }

  function removeValue(index: number) {
    onChange(values.filter((_, i) => i !== index));
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      addValue();
      return;
    }
    // Backspace on empty input removes last chip
    if (e.key === "Backspace" && inputValue === "" && values.length > 0) {
      onChange(values.slice(0, -1));
    }
  }

  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-1 rounded-md border bg-background px-2 py-1 min-h-[2rem] flex-1 min-w-0 cursor-text",
        disabled && "opacity-60 cursor-not-allowed"
      )}
      onClick={() => !disabled && inputRef.current?.focus()}
      data-testid="enum-values-input"
    >
      {values.map((v, i) => (
        <Badge
          key={`${v}-${i}`}
          variant="secondary"
          className="gap-0.5 pr-0.5 text-xs h-5 flex-shrink-0"
        >
          <span>{v}</span>
          <button
            type="button"
            aria-label={`Remove ${v}`}
            disabled={disabled}
            className="ml-0.5 rounded-full hover:bg-muted-foreground/20 p-0.5 disabled:cursor-not-allowed"
            onClick={(e) => {
              e.stopPropagation();
              removeValue(i);
            }}
          >
            <X className="w-2.5 h-2.5" />
          </button>
        </Badge>
      ))}

      <div className="flex items-center gap-1 flex-1 min-w-[6rem]">
        <Input
          ref={inputRef}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={values.length === 0 ? "Add values…" : ""}
          disabled={disabled}
          aria-label="Enum values"
          className="h-6 border-0 shadow-none focus-visible:ring-0 p-0 text-xs bg-transparent flex-1 min-w-0"
        />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={disabled || !inputValue.trim()}
          onClick={addValue}
          className="h-6 px-1.5 text-xs flex-shrink-0"
          aria-label="Add enum value"
        >
          Add
        </Button>
      </div>
    </div>
  );
}
